import {
  Action,
  Actions,
  Alert,
  AlertProps,
  DropdownTagProps,
  FlagProps,
  IconButton,
  openDialog,
  Parameter,
  Parameters,
  Separator,
  Tooltip,
  TooltipProps,
} from 'platform/components';
import {
  Box,
  FlexboxAlign,
  Heading,
  HStack,
  Show,
  Space,
  Text,
  ThemeIconKey,
  ValueByDevice,
  VStack,
} from 'platform/foundation';

import {Fragment} from 'react/jsx-runtime';

import {and, or} from 'ramda';
import {isArray, isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {useSmsGetKonzultaCredentialsQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {DOT_CHARACTER, suffixTestId, TestIdProps} from 'shared/web';

import {useEmailConfig} from '../../hooks/useEmailConfig';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {VehicleConditionEvaluationType} from '../../hooks/useVehicleConditionEvaluation';
import {SendCustomerEmailForm} from '../SendCustomerEmailForm/SendCustomerEmailForm';
import {SendCustomerSmsForm} from '../SendCustomerSmsForm/SendCustomerSmsForm';
import {EntityHeaderControl, EntityHeaderControls} from './components/EntityHeaderControls';
import {EntityHeaderFlags} from './components/EntityHeaderFlags';
import {EntityHeaderImage} from './components/EntityHeaderImage';
import {ListEditableTags} from './components/labels/ListEditableTags';
import {ParameterBlock} from './components/ParameterBlock';
import {SkeletonLoadingBox} from './components/SkeletonLoadingBox';
import {SnippetProps} from './components/Snippet';
import {SnippetPanel} from './components/SnippetPanel';
import {VehicleConditionEvaluation} from './components/VehicleConditionEvaluation';
import {HeaderPermissions} from './types/headerPermissions';

interface AdditionalParameterBlock {
  primaryParameter: string;
  secondaryParameter: string;
}

export interface EntityHeaderFlag extends Omit<FlagProps, 'size'> {
  tooltip?: TooltipProps;
}

export interface EntityHeaderDropdownFlag extends Omit<DropdownTagProps, 'size'> {
  tooltip?: TooltipProps;
}

export interface EntityHeaderProps extends TestIdProps {
  image?: string;
  imageRatio?: `${number}/${number}`;
  icon?: ThemeIconKey;
  flags?: EntityHeaderFlag[];
  dropdownFlags?: EntityHeaderDropdownFlag[];
  title?: string;
  parameters?: Parameter[];
  parametersAlign?: FlexboxAlign | ValueByDevice<FlexboxAlign>;
  isEntityLoading?: boolean;
  isLoadingSnippets?: boolean;
  primaryParameter?: string;
  secondaryParameter?: string;
  tertiaryParameter?: string;
  additionalParameterBlocks?: AdditionalParameterBlock[];
  snippets?: SnippetProps[];
  actions?: Action[];
  resourceId?: EntityResourceIds;
  recordId?: string;
  onImageClick?: () => void;
  controls?: EntityHeaderControl[];
  vehicleConditionEvaluation?: VehicleConditionEvaluationType;
  permissions?: HeaderPermissions;
  alerts?: AlertProps[];
  parkingRecordId?: string;
  authorizationProfileRecordId?: string;
  enableTeams?: boolean;
  isAuthorizationProfileEditable?: boolean;
  onAuthorizationProfileChange?: () => void;
}

export function EntityHeader(props: EntityHeaderProps) {
  const {isEnabled: isEmailConfigEnabled} = useEmailConfig();
  const {data: konzultaConfig} = useSmsGetKonzultaCredentialsQuery();

  const [canReadBusinessCase] = usePermissions({
    permissionKeys: ['businessCaseRead'],
  });

  const hasAnyParameter =
    props.primaryParameter || props.secondaryParameter || props.tertiaryParameter;

  const hasFlags = isNotNilOrEmpty(props.flags);

  return (
    <>
      <Box padding={4} backgroundColor="palettes.white.10.100">
        <VStack>
          <HStack spacing={4} align="stretch">
            <EntityHeaderImage
              icon={props.icon}
              image={props.image}
              ratio={props.imageRatio}
              onImageClick={props.onImageClick}
              data-testid={suffixTestId('entityHeader-image', props)}
            />
            <VStack spacing={2}>
              <Show
                when={or(
                  isNotNil(props.flags),
                  and(or(isNotNil(props.resourceId), isNotNil(props.recordId)))
                )}
              >
                <HStack spacing={1} align="center" wrap>
                  <EntityHeaderFlags
                    flags={props.flags}
                    dropdownFlags={props.dropdownFlags}
                    isLoading={props.isEntityLoading}
                    data-testid={props['data-testid']}
                  />
                  <Show when={props.recordId && props.resourceId && hasFlags}>
                    <Text color="tertiary">{DOT_CHARACTER}</Text>
                  </Show>
                  <Show when={props.recordId && props.resourceId}>
                    <ListEditableTags
                      size="default"
                      isLoading={props.isEntityLoading}
                      buttonVariant="secondary"
                      recordId={props.recordId as string}
                      resourceId={props.resourceId as EntityResourceIds}
                      data-testid={suffixTestId('entityHeader-editable-tags', props)}
                      isTagDeletable={props.permissions?.tag?.isDeletable}
                      isTagUpdatable={props.permissions?.tag?.isUpdatable}
                      isTagReadable={props.permissions?.tag?.isReadable}
                      isTagCreatable={props.permissions?.tag?.isCreatable}
                      isTagAssignable={props.permissions?.tag?.isAssignable}
                      isTagUnassignable={props.permissions?.tag?.isUnassignable}
                    />
                  </Show>
                </HStack>
              </Show>
              <SkeletonLoadingBox isLoading={props.isEntityLoading} height={8} width={120}>
                <Heading size={2} data-testid={suffixTestId('entityHeader-title', props)}>
                  {props.title}
                </Heading>
              </SkeletonLoadingBox>
              <HStack spacing={1}>
                <SkeletonLoadingBox isLoading={props.isEntityLoading} height={5} width={40}>
                  <Parameters
                    size="small"
                    color="secondary"
                    parameters={props.parameters}
                    align={props.parametersAlign}
                    data-testid={suffixTestId('entityHeader-parameters', props)}
                  />
                </SkeletonLoadingBox>
                <Show when={canReadBusinessCase}>
                  <SkeletonLoadingBox isLoading={props.isEntityLoading} height={5} width={40}>
                    <VehicleConditionEvaluation
                      size="small"
                      color="secondary"
                      vehicleCondition={props.vehicleConditionEvaluation}
                      data-testid={suffixTestId('entityHeader-vehicle-condition', props)}
                    />
                  </SkeletonLoadingBox>
                </Show>
              </HStack>
              <Show when={and(isNotNil(props.recordId), props?.controls?.length)}>
                <EntityHeaderControls
                  controls={props.controls!}
                  resourceId={props.resourceId}
                  recordId={props.recordId as string}
                  parkingRecordId={props.parkingRecordId}
                  authorizationProfileRecordId={props.authorizationProfileRecordId}
                  isAuthorizationProfileEditable={props.isAuthorizationProfileEditable}
                  onAuthorizationProfileChange={props.onAuthorizationProfileChange}
                  permissions={props.permissions}
                  enableTeams={props.enableTeams}
                  data-testid={suffixTestId('entityHeader-controls', props)}
                />
              </Show>
            </VStack>
            <Space fillAvailable />
            <VStack align="flex-end" justify="space-between" spacing={2}>
              <Show when={isNotNilOrEmpty(props?.actions)}>
                <SkeletonLoadingBox isLoading={props.isEntityLoading} width={30} height={8}>
                  <Actions
                    actions={props.actions}
                    data-testid={suffixTestId('entityHeader-actions', props)}
                  />
                </SkeletonLoadingBox>
              </Show>

              <HStack spacing={4}>
                <Show when={props.resourceId === 'VEHICLE' && props.additionalParameterBlocks}>
                  {props.additionalParameterBlocks?.map((block) => (
                    <ParameterBlock
                      key={block.primaryParameter}
                      primaryParameter={block.primaryParameter}
                      secondaryParameter={block.secondaryParameter}
                      resourceId={props.resourceId}
                      recordId={props.recordId}
                      konzultaConfig={konzultaConfig}
                      isEmailConfigEnabled={isEmailConfigEnabled}
                    />
                  ))}
                </Show>

                <Show
                  when={
                    hasAnyParameter &&
                    props.resourceId === 'VEHICLE' &&
                    props.additionalParameterBlocks
                  }
                >
                  <Separator orientation="vertical" />
                </Show>

                <Show when={hasAnyParameter}>
                  <VStack spacing={1} align="flex-end">
                    <Show when={props.primaryParameter}>
                      <HStack spacing={1} align="center">
                        <Heading
                          size={3}
                          isSingleLine
                          data-testid={suffixTestId('entityHeader-primaryParameter', props)}
                        >
                          {props.primaryParameter}
                        </Heading>
                        <Show
                          when={
                            props.resourceId === 'CUSTOMER' &&
                            props.primaryParameter !==
                              i18n.t('entity.customer.labels.noPhoneNumber')
                          }
                        >
                          <Tooltip
                            label={
                              konzultaConfig?.enabled
                                ? i18n.t('general.actions.sendMessage')
                                : i18n.t('general.notifications.setupSmsProvider')
                            }
                            placement="top-end"
                          >
                            <IconButton
                              severity="informational"
                              data-testid="open-send-message-modal"
                              icon="communication/textsms"
                              isDisabled={!konzultaConfig?.enabled}
                              onClick={() =>
                                openDialog(
                                  <SendCustomerSmsForm
                                    customerId={props.recordId}
                                    recordId={props.recordId}
                                    resourceId={props.resourceId}
                                  />,
                                  {
                                    scrollBehavior: 'outside',
                                    size: 'large',
                                    title: i18n.t('general.labels.smsMessage'),
                                  }
                                )
                              }
                            />
                          </Tooltip>
                        </Show>
                        <Show when={isNotNilOrEmpty(props.tertiaryParameter)}>
                          <Heading
                            size={3}
                            isSingleLine
                            data-testid={suffixTestId('entityHeader-tertiaryParameter', props)}
                          >
                            {`/ ${props.tertiaryParameter ?? ''}`}
                          </Heading>
                        </Show>
                      </HStack>
                    </Show>
                    <Show when={props.secondaryParameter}>
                      <HStack spacing={1} align="center">
                        <Text
                          size="base"
                          color="secondary"
                          noWrap
                          data-testid={suffixTestId('entityHeader-secondaryParameter', props)}
                        >
                          {props.secondaryParameter}
                        </Text>
                        <Show
                          when={
                            props.resourceId === 'CUSTOMER' &&
                            props.secondaryParameter !== i18n.t('entity.customer.labels.noEmail')
                          }
                        >
                          <Tooltip
                            label={
                              isEmailConfigEnabled
                                ? i18n.t('general.actions.sendEmail')
                                : i18n.t('general.notifications.setupEmailProvider')
                            }
                            placement="top-end"
                          >
                            <IconButton
                              severity="informational"
                              data-testid="open-send-email-modal"
                              icon="communication/email"
                              isDisabled={!isEmailConfigEnabled}
                              onClick={() =>
                                openDialog(
                                  <SendCustomerEmailForm
                                    customerId={props.recordId}
                                    recordId={props.recordId}
                                    resourceId={props.resourceId}
                                  />,
                                  {
                                    scrollBehavior: 'outside',
                                    size: 'large',
                                    title: i18n.t('general.actions.sendEmail'),
                                  }
                                )
                              }
                            />
                          </Tooltip>
                        </Show>
                      </HStack>
                    </Show>
                  </VStack>
                </Show>
              </HStack>
            </VStack>
          </HStack>
          <Show when={props.snippets}>
            <Space vertical={4} />
            <SnippetPanel
              snippets={props.snippets}
              areSnippetsLoading={props.isLoadingSnippets}
              resourceId={props.resourceId}
              data-testid={suffixTestId('entityHeader-snippets', props)}
            />
          </Show>
        </VStack>
      </Box>
      <Show when={props.alerts}>
        {props.alerts?.map((alert, index) => (
          <Fragment key={isArray(alert.message) ? alert.message?.[0] : alert.message}>
            <Alert {...alert} data-testid={suffixTestId(`entityHeader-alert-[${index}]`, props)} />
            {index < (props.alerts?.length ?? 0) - 1 && <Space vertical={1} />}
          </Fragment>
        ))}
      </Show>
    </>
  );
}
